/**
 * ReferringProviderSelect
 *
 * Searchable grouped dropdown for picking a clinic user as the referrer
 * on a guided order or appointment. Fetches the unified referrer list
 * from `/clinics/:clinicId/commissions/referrers` and groups by
 * primary_role (practitioner / clinic_admin / other).
 */

import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  Check,
  ChevronsUpDown,
  Loader2,
  Stethoscope,
  User,
  ShieldCheck,
  Users,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  getReferrers,
  getEffectiveRate,
  bpsToPercentage,
  Referrer,
  ReferrerRole,
} from '@/services/commissionService'

interface ReferringProviderSelectProps {
  clinicId: string
  value?: {
    referringUserId?: string | null
    commissionRateBps?: number | null
  }
  onChange: (value: {
    referringUserId: string | null
    commissionRateBps: number | null
  }) => void
  disabled?: boolean
  showCommissionRate?: boolean
}

const ROLE_ORDER: ReferrerRole[] = [
  'practitioner',
  'clinic_admin',
]

const ROLE_LABELS: Record<string, string> = {
  practitioner: 'Practitioners',
  clinic_admin: 'Clinic Admins',
  admin: 'Admins',
  super_admin: 'System Admins',
}

const ROLE_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  practitioner: Stethoscope,
  clinic_admin: ShieldCheck,
  admin: ShieldCheck,
  super_admin: ShieldCheck,
}

const groupByRole = (referrers: Referrer[]) => {
  const groups = new Map<string, Referrer[]>()
  for (const r of referrers) {
    const key = r.primary_role || 'other'
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(r)
  }

  // Order: known roles first in ROLE_ORDER, then everything else alphabetical
  const knownOrdered = ROLE_ORDER.filter(role => groups.has(role))
  const rest = [...groups.keys()]
    .filter(k => !ROLE_ORDER.includes(k as ReferrerRole))
    .sort()
  return [...knownOrdered, ...rest].map(role => ({
    role,
    label: ROLE_LABELS[role] || role,
    icon: ROLE_ICON[role] || Users,
    users: (groups.get(role) || []).sort((a, b) => a.name.localeCompare(b.name)),
  }))
}

export function ReferringProviderSelect({
  clinicId,
  value,
  onChange,
  disabled = false,
  showCommissionRate = true,
}: ReferringProviderSelectProps) {
  const [commissionRate, setCommissionRate] = useState<number | null>(null)
  const [open, setOpen] = useState(false)

  const { data: referrers, isLoading } = useQuery({
    queryKey: ['referrers', clinicId],
    queryFn: () => getReferrers(clinicId),
    enabled: !!clinicId,
  })

  useEffect(() => {
    if (value?.commissionRateBps != null) {
      setCommissionRate(bpsToPercentage(value.commissionRateBps))
    } else {
      setCommissionRate(null)
    }
  }, [value?.commissionRateBps])

  const groups = useMemo(() => groupByRole(referrers || []), [referrers])
  const hasReferrers = (referrers?.length || 0) > 0
  const selectedReferrer = useMemo(
    () =>
      value?.referringUserId
        ? referrers?.find(r => r.user_id === value.referringUserId) || null
        : null,
    [value?.referringUserId, referrers],
  )
  const SelectedIcon = selectedReferrer
    ? ROLE_ICON[selectedReferrer.primary_role || 'other'] || Users
    : Users

  // Radix Select forbids SelectItem value="" (empty is reserved for the
  // "no selection" state). Use a sentinel for the "None" option and map
  // it back to null in the handler.
  const NONE_VALUE = '__none__'
  const selectedUserId = value?.referringUserId || NONE_VALUE

  const handleReferrerChange = async (newValue: string) => {
    if (!newValue || newValue === NONE_VALUE) {
      setCommissionRate(null)
      onChange({ referringUserId: null, commissionRateBps: null })
      return
    }

    const picked = referrers?.find(r => r.user_id === newValue)
    if (picked?.effective_rate_bps != null) {
      setCommissionRate(bpsToPercentage(picked.effective_rate_bps))
      onChange({
        referringUserId: newValue,
        commissionRateBps: picked.effective_rate_bps,
      })
      return
    }

    try {
      const rateData = await getEffectiveRate(clinicId, newValue)
      setCommissionRate(rateData.ratePercentage)
      onChange({
        referringUserId: newValue,
        commissionRateBps: rateData.rateBps,
      })
    } catch {
      setCommissionRate(20)
      onChange({ referringUserId: newValue, commissionRateBps: 2000 })
    }
  }

  const resolvedUserId =
    selectedUserId && selectedUserId !== NONE_VALUE ? selectedUserId : null

  const handleRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    if (raw === '') {
      setCommissionRate(null)
      onChange({ referringUserId: resolvedUserId, commissionRateBps: null })
      return
    }
    const percentage = parseFloat(raw)
    if (Number.isNaN(percentage)) return
    setCommissionRate(percentage)
    const inRange = percentage >= 1 && percentage <= 50
    onChange({
      referringUserId: resolvedUserId,
      commissionRateBps: inRange ? Math.round(percentage * 100) : null,
    })
  }

  const handleRateBlur = () => {
    if (commissionRate == null) return
    const clamped = Math.min(Math.max(commissionRate, 1), 50)
    if (clamped === commissionRate) return
    setCommissionRate(clamped)
    onChange({
      referringUserId: resolvedUserId,
      commissionRateBps: Math.round(clamped * 100),
    })
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="referring-provider">Referring Staff (Optional)</Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              id="referring-provider"
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled || isLoading}
              className="w-full justify-between font-normal"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading...
                </span>
              ) : selectedReferrer ? (
                <span className="flex items-center gap-2 truncate">
                  <SelectedIcon className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="truncate">{selectedReferrer.name}</span>
                  {selectedReferrer.credentials && (
                    <span className="text-xs text-muted-foreground">
                      , {selectedReferrer.credentials}
                    </span>
                  )}
                </span>
              ) : (
                <span className="text-muted-foreground">
                  Select referring staff
                </span>
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[--radix-popover-trigger-width] p-0"
            align="start"
          >
            <Command>
              <CommandInput placeholder="Search staff…" className="h-9" />
              <CommandList>
                <CommandEmpty>No staff found.</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    value="none"
                    onSelect={() => {
                      handleReferrerChange(NONE_VALUE)
                      setOpen(false)
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        selectedUserId === NONE_VALUE
                          ? 'opacity-100'
                          : 'opacity-0',
                      )}
                    />
                    None
                  </CommandItem>
                </CommandGroup>
                {groups.map(group => {
                  const Icon = group.icon
                  return (
                    <CommandGroup key={group.role} heading={group.label}>
                      {group.users.map(r => (
                        <CommandItem
                          key={r.user_id}
                          value={`${r.name} ${r.credentials || ''} ${
                            r.email || ''
                          }`}
                          onSelect={() => {
                            handleReferrerChange(r.user_id)
                            setOpen(false)
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              selectedUserId === r.user_id
                                ? 'opacity-100'
                                : 'opacity-0',
                            )}
                          />
                          <Icon className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                          <span className="truncate">{r.name}</span>
                          {r.credentials && (
                            <span className="ml-1 text-xs text-muted-foreground">
                              , {r.credentials}
                            </span>
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )
                })}
                {!hasReferrers && !isLoading && (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">
                    No referrers configured
                  </div>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {showCommissionRate && resolvedUserId && (
        <div className="space-y-2">
          <Label htmlFor="commission-rate">
            Commission Rate (%) <span className="text-destructive">*</span>
          </Label>
          <div className="flex items-center gap-2">
            <Input
              id="commission-rate"
              type="number"
              min={1}
              max={50}
              step={0.5}
              value={commissionRate ?? ''}
              onChange={handleRateChange}
              onBlur={handleRateBlur}
              disabled={disabled}
              required
              aria-invalid={commissionRate == null}
              className="w-24"
            />
            <span className="text-sm text-muted-foreground">(1% - 50%)</span>
          </div>
          {commissionRate == null ? (
            <p className="text-xs text-destructive">
              Commission rate is required when a referring staff is selected.
            </p>
          ) : commissionRate < 1 || commissionRate > 50 ? (
            <p className="text-xs text-destructive">
              Commission rate must be between 1% and 50%.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Commission is calculated as {commissionRate}% of clinic revenue
              for this order.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default ReferringProviderSelect
